import { z } from 'zod';

import { CivilDateDto } from './civilDate';
import { refineTrainerFee, trainerFeeFields } from './trainer-fee';
import { isRealCivilDay } from '../utils/date';

/**
 * A package price in major units of its currency. Capped at one billion
 * (residuals Task 2, fix round 1): far above any real gym price, and far
 * below 2^46 — past that, a price's minor units no longer convert back to a
 * major-unit number exactly (`minorToMajor`), and the desk dialogs that
 * prefill or bound amounts from it could not show them.
 */
export const MEMBERSHIP_PRICE_MAX = 1_000_000_000;

const MembershipPriceDto = z
  .number()
  .nonnegative('VALIDATION__NonNegative')
  .max(MEMBERSHIP_PRICE_MAX, 'VALIDATION__Max');

export const CreateMembershipDto = z
  .object({
    name: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit'),
    price: MembershipPriceDto,
    currency: z.enum(['USD', 'EUR', 'RSD', 'BAM']).optional(),
    collaborationModeA: z.enum(['LIVE', 'ONLINE', 'HYBRID']),
    duration: z.number().int().positive('VALIDATION__Positive'),
    durationUnit: z.enum(['DAYS', 'WEEKS', 'MONTHS']),
    numberOfWorkouts: z.number().int().positive().nullable().optional(),
    // A trainer package / day pass with a trainer (1.11, 1.12)
    includesTrainer: z.boolean().optional(),
    description: z.string().max(2000).optional(),
    tagIds: z.array(z.string().uuid()).optional(),
    // MV2-577 — the package's default trainer fee (needs trainer_fees_manage)
    ...trainerFeeFields,
  })
  .superRefine(refineTrainerFee);

export type ApiCreateMembershipDto = z.infer<typeof CreateMembershipDto>;

export const UpdateMembershipDto = z
  .object({
    name: z
      .string()
      .min(1, 'VALIDATION__Required')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    price: MembershipPriceDto.optional(),
    currency: z.enum(['USD', 'EUR', 'RSD', 'BAM']).optional(),
    collaborationModeA: z.enum(['LIVE', 'ONLINE', 'HYBRID']).optional(),
    duration: z.number().int().positive('VALIDATION__Positive').optional(),
    durationUnit: z.enum(['DAYS', 'WEEKS', 'MONTHS']).optional(),
    numberOfWorkouts: z.number().int().positive().nullable().optional(),
    includesTrainer: z.boolean().optional(),
    description: z.string().max(2000).nullable().optional(),
    tagIds: z.array(z.string().uuid()).optional(),
    // MV2-577 — NOT a package term: free to change on a sold package
    ...trainerFeeFields,
  })
  .strict()
  .superRefine(refineTrainerFee);

export type ApiUpdateMembershipDto = z.infer<typeof UpdateMembershipDto>;

export const RenameMembershipDto = z.object({
  name: z
    .string()
    .min(1, 'VALIDATION__Required')
    .max(255, 'VALIDATION__Max255CharLimit'),
});

export type ApiRenameMembershipDto = z.infer<typeof RenameMembershipDto>;

// T10 (spec §3.3): the client membership card's own selection, moved to the
// server so the totals beside the list describe exactly the list.
//
// The days are BARE civil days ('yyyy-MM-dd'), deliberately NOT `CivilDateDto`:
// `UserMemberships.startDate` is a DATEONLY column, so the selection is a
// string comparison on civil days and never an instant. `CivilDateDto` would
// also accept a legacy ISO instant and normalize it, which would silently
// answer a different question than the one the picker asked.
//
// A bare `?membershipIds=<id>` arrives as a string, not a one-element array —
// Express only produces an array for a REPEATED key, which is what the
// frontend's own serializer emits (`queryParamsFromString`, one `key=value`
// pair per item). The comma split is tolerated too, exactly like
// `ListScheduleSearchQueryDto`, so a hand-written URL works either way.
const membershipQueryDay = z
  .string()
  .refine(isRealCivilDay, { message: 'VALIDATION__InvalidDate' });

const toMembershipIdArray = (value: unknown) => {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value;
  return typeof value === 'string' ? value.split(',') : [value];
};

export const ClientMembershipsQueryDto = z
  .object({
    from: membershipQueryDay.optional(),
    to: membershipQueryDay.optional(),
    membershipIds: z
      .preprocess(toMembershipIdArray, z.array(z.string().uuid()).min(1))
      .optional(),
  })
  .strict()
  // A half-open window needs BOTH ends: one alone is not a narrower window,
  // it is an open-ended one the card never asks for and the summary could not
  // describe (the per-month average divides by the window's own length).
  .refine((query) => (query.from === undefined) === (query.to === undefined), {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  })
  // [from, to) — an empty or inverted window is a client bug, not a filter
  // that legitimately selects nothing. Civil days compare lexicographically.
  .refine((query) => !query.from || !query.to || query.from < query.to, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  });

export type ApiClientMembershipsQueryDto = z.infer<
  typeof ClientMembershipsQueryDto
>;

// MV2-573: SINGLE is a one-off term with no auto-renewal; RECURRING renews on
// its own billing cycle. `requiresCardLinkNow` is independent of `type` — a
// RECURRING membership does not itself demand a card (a trainer may run it on
// trust, per Aleksandar's ticket comment), so the flag is what actually gates
// the client into `PENDING_PAYMENT_METHOD` at assignment time.
const membershipTypeFields = {
  type: z.enum(['SINGLE', 'RECURRING']).optional().default('SINGLE'),
  requiresCardLinkNow: z.boolean().optional().default(false),
};

const onlyRecurringRequiresCardLink = <
  T extends { type: 'SINGLE' | 'RECURRING'; requiresCardLinkNow: boolean },
>(
  data: T,
) => data.type === 'RECURRING' || !data.requiresCardLinkNow;

// Task 2 (membership-renewal plan) — `autoRenew` only means anything on a
// RECURRING row that does not itself demand the card now: a card-managed
// row is never cardless auto-renewal (its subscription IS the renewal), and
// SINGLE has no next cycle to renew. Only an attempt to ENABLE it there
// (`true`) is refused — a caller belief the server refuses loudly rather
// than silently dropping. Absent or `false` is always fine: the desk form
// sends `false` for every assignment the switch does not apply to, and the
// writer (`resolveAutoRenewOnAssign`) stores `false` for such a row anyway
// (residuals Task 2, fix round 1: refusing `false` turned every SINGLE and
// card-now desk assignment into a 400). Exported for the invite rider
// (`OrganizationUserInviteDto`), which maps its own field names onto it.
export const onlyEligibleAutoRenew = <
  T extends {
    type: 'SINGLE' | 'RECURRING';
    requiresCardLinkNow: boolean;
    autoRenew?: boolean;
  },
>(
  data: T,
) =>
  data.autoRenew !== true ||
  (data.type === 'RECURRING' && !data.requiresCardLinkNow);

/**
 * The NEW package a desk assigns — shared by a plain assignment and by
 * "Zameni odmah" (Task 9), so the two can never describe the sale
 * differently. The start date is not here: an assignment chooses it, an
 * immediate replacement starts on the organization's today.
 */
const assignmentChoiceFields = {
  membershipId: z.string().uuid(),
  discountPercent: z.number().min(0).max(100).optional().default(0),
  notes: z.string().max(2000).optional(),
  /** Foundation 2 §6.1 — the desk may correct the visit count while assigning
   *  (a gym membership's default is one visit per day of the period). Each
   *  bound names a catalog key: `ZodFilter` translates nothing else (live
   *  test O4 — the 422 read "Unknown, message could not be translated"). */
  numberOfWorkouts: z
    .number()
    .int('VALIDATION__WholeNumber')
    .min(1, 'VALIDATION__Positive')
    .max(10000, 'VALIDATION__Max')
    .optional(),
  /** Foundation 2 §5.2 — one of the client's trainers in this organization, or
   *  `null` for "no trainer". Absent means "apply the default rule". */
  trainerId: z.string().uuid().nullable().optional(),
  /** Task 2 — absent means the writer's own default (true when eligible,
   *  else false); explicit `false` opts out of an eligible default (and is
   *  accepted, as a no-op, on an ineligible one); `true` needs eligibility. */
  autoRenew: z.boolean().optional(),
  /** MV2-577 (D3) — absent takes the package's default trainer fee; a null
   *  pair means "no fee"; a set pair overrides it. Explicit input needs
   *  `trainer_fees_manage` (asserted in `prepareAssignmentTerms`). Each
   *  composed DTO applies `refineTrainerFee`. */
  ...trainerFeeFields,
  ...membershipTypeFields,
};

export const AssignClientMembershipDto = z
  .object({
    ...assignmentChoiceFields,
    startDate: CivilDateDto,
    // MV2-573 §5: "replace at next billing cycle" (Aleksandar's own
    // recommendation, the cleanest option) — traceability only, on the NEW
    // row. The controller enforces startDate >= the replaced row's endDate;
    // this DTO only shapes the wire, not that cross-row rule.
    replacesUserMembershipId: z.string().uuid().optional(),
  })
  .refine(onlyRecurringRequiresCardLink, {
    message: 'VALIDATION__RequiresCardLinkOnlyForRecurring',
    path: ['requiresCardLinkNow'],
  })
  .refine(onlyEligibleAutoRenew, {
    message: 'VALIDATION__AutoRenewOnlyForCardlessRecurring',
    path: ['autoRenew'],
  })
  .superRefine(refineTrainerFee);

export type ApiAssignClientMembershipDto = z.infer<
  typeof AssignClientMembershipDto
>;

/**
 * Celina 1.5 (question 4, PO 22.09.) — an assigned membership keeps its sale:
 * only the note, the trainer and (before the first visit, with no live
 * subscription — `MembershipService.startDateLocks`) the start date change.
 * STRICT on purpose: a caller that still sends `membershipId` or
 * `discountPercent` believes it switches the package or the price; a 400
 * says it does not, where a stripped key would say nothing.
 */
export const EditClientMembershipDto = z
  .object({
    /** Absent keeps the note; `null` or `''` clears it. */
    notes: z.string().max(2000).nullable().optional(),
    /** Absent, or the stored day, moves nothing. */
    startDate: CivilDateDto.optional(),
    /** Sent only when the desk changed it: absent keeps the stored trainer,
     *  even one no longer linked to the client; `null` clears it. */
    trainerId: z.string().uuid().nullable().optional(),
    /**
     * Task 2 — the cardless auto-renew preference, NOT part of the locked
     * sale: staff may flip it on/off any time, unlike price/package/discount.
     * The DTO cannot see the row's own `type`/`requiresCardLinkNow`/linked
     * subscription, so eligibility for switching it ON is checked in the
     * controller against the stored row, not here.
     */
    autoRenew: z.boolean().optional(),
    /** MV2-577 — the assignment's trainer fee: absent keeps it, a null pair
     *  clears it. Not part of the locked sale; needs `trainer_fees_manage`
     *  (asserted in `MembershipService.editUserMembership`). */
    ...trainerFeeFields,
  })
  .strict()
  .superRefine(refineTrainerFee);

export type ApiEditClientMembershipDto = z.infer<
  typeof EditClientMembershipDto
>;

/**
 * The server-computed facts a client membership list row carries for the
 * card's actions (none of them money — they survive redaction). Each one is
 * the server's own answer, so the card offers an action exactly where the
 * route would accept it.
 */
export interface ClientMembershipRowActions {
  /** A card subscription is linked (any status, CANCELED included). */
  cardManaged: boolean;
  /** A live successor exists (Ruling A). */
  hasSuccessor: boolean;
  /** "Undo renewal" would succeed. */
  canUndoRenewal: boolean;
  /** "Schedule activation" would be accepted. */
  canScheduleActivation: boolean;
  /** A scheduled activation exists and clearing it would be accepted — also
   *  while membership card billing is off (10.1, review 2026-10-01 #1). */
  canClearScheduledActivation: boolean;
  /** The client's own "Link card & activate" would be accepted
   *  (`clientCardLinkRefusal`). */
  canLinkCard: boolean;
  /** Staff's "Send the card request again" would be accepted
   *  (`cardSetupResendRefusal`). */
  canResendCardSetup: boolean;
  /** The days a scheduled activation may take, `to` exclusive; null when
   *  scheduling is refused. */
  activationWindow: { from: string; to: string } | null;
  /** "Freeze" would be accepted (Ruling AK). */
  canFreeze: boolean;
  /** AL-1 — the organization's civil day (yyyy-MM-dd) of a first card charge
   *  still to come (a card-now row whose card was linked early); null
   *  otherwise. */
  firstChargeOn: string | null;
}

/**
 * Celina 1.5 (question 4) — why an assignment's start date may no longer
 * move: it already has a visit, or a card subscription still charges it.
 * Live test 3 (N7) — or it was ended early (End cycle, Replace now, a VISITS
 * renewal): a closed cycle never reopens by moving its start.
 * `null` on the wire = the date is editable.
 */
export const START_DATE_LOCKS = [
  'ENDED_EARLY',
  'VISITS',
  'SUBSCRIPTION',
] as const;
export type StartDateLock = (typeof START_DATE_LOCKS)[number];

export const AssignMembershipBulkDto = z
  .object({
    // Every requested group is expanded to its currently active members and
    // merged with the directly requested clients, de-duped — same contract as
    // `ScheduleTargetsService.resolve` (schedule/nutrition assignment).
    clientIds: z.array(z.string().uuid()).optional().default([]),
    groupIds: z.array(z.string().uuid()).optional().default([]),
    startDate: CivilDateDto,
    discountPercent: z.number().min(0).max(100).optional().default(0),
    notes: z.string().max(2000).optional(),
    /** Foundation 2 §6.1 — one visit override for every row of the batch;
     *  absent means the package's own count, or the unit default. */
    numberOfWorkouts: z
      .number()
      .int('VALIDATION__WholeNumber')
      .min(1, 'VALIDATION__Positive')
      .max(10000, 'VALIDATION__Max')
      .optional(),
    /** Task 2 — same rule as the single-assign DTO, applied to the whole
     *  batch: absent means the writer's own default per row. */
    autoRenew: z.boolean().optional(),
    ...membershipTypeFields,
  })
  .refine((data) => data.clientIds.length > 0 || data.groupIds.length > 0, {
    message: 'VALIDATION__Required',
    path: ['clientIds'],
  })
  .refine(onlyRecurringRequiresCardLink, {
    message: 'VALIDATION__RequiresCardLinkOnlyForRecurring',
    path: ['requiresCardLinkNow'],
  })
  .refine(onlyEligibleAutoRenew, {
    message: 'VALIDATION__AutoRenewOnlyForCardlessRecurring',
    path: ['autoRenew'],
  });

export type ApiAssignMembershipBulkDto = z.infer<
  typeof AssignMembershipBulkDto
>;

export const AddMembershipPaymentDto = z.object({
  amount: z.number().positive('VALIDATION__Positive'),
  // absent = the assignment's own currency (Foundation 2 §6.1), resolved by
  // the controller — never a hard-coded default
  currency: z.enum(['USD', 'EUR', 'RSD', 'BAM']).optional(),
  paymentDate: z.string().datetime({ offset: true }),
  paymentMethod: z.enum(['CASH', 'CARD', 'TRANSFER', 'OTHER']),
  notes: z.string().max(2000).optional(),
});

export type ApiAddMembershipPaymentDto = z.infer<
  typeof AddMembershipPaymentDto
>;

// Task 8 (membership-renewal plan) — "the rest is a discount, the client owes
// nothing": one append-only discount on a cycle, in the cycle's OWN currency
// (there is deliberately no currency field). Exactness in minor units and
// "not more than is due" are the server's checks, made under the cycle's
// billing lock. Strict: a caller sending a currency or a kind believes it
// chooses something it does not.
export const AddCycleDiscountDto = z
  .object({
    amount: z.number().positive('VALIDATION__Positive'),
    reason: z.string().trim().min(1, 'VALIDATION__Required').max(500),
  })
  .strict();

export type ApiAddCycleDiscountDto = z.infer<typeof AddCycleDiscountDto>;

// Task 4 (membership-renewal plan) — `consumesVisit: false` is the PO's
// "check-in for self-training" option: staff record attendance without
// spending a visit off the package. Optional, defaults to `true` (the
// pre-existing behaviour) when omitted — see `MembershipService.createCheckIn`.
export const CreateCheckInDto = z.object({
  checkedInAt: z.string().datetime({ offset: true }),
  notes: z.string().max(2000).optional(),
  consumesVisit: z.boolean().optional(),
  // Set once the FE has shown the caller the membership's existing check-in
  // for today and they chose to record another one anyway. Omitted/`false`
  // keeps the one-per-day guard in `MembershipService.createCheckIn`.
  confirmDuplicate: z.boolean().optional(),
  // "Probni trening" — mark this arrival as the client's trial workout.
  isTrial: z.boolean().optional(),
  // The booking (seat) of this membership the trainer picked as the event the
  // client is arriving for. Omitted keeps the same-day automatic link.
  bookingId: z.string().uuid().optional(),
});

export type ApiCreateCheckInDto = z.infer<typeof CreateCheckInDto>;

// T5 (D9, spec §3.7.1): cancellation and refund are two DIFFERENT actions.
//
// The body carries NOTHING. A cancellation changes service/access and says
// nothing about money; an executed refund is attested separately through
// `POST .../memberships/:userMembershipId/refunds`, which posts a linked
// refund child against the original charge. The obsolete `refundAmount`
// toggle used to write `UserMemberships.refundAmount` + `CANCELED_REFUND`
// with no ledger row behind it — a money claim nothing could back.
//
// `.strict()` is what makes the removal visible instead of silent: a client
// still sending `refundAmount` gets a 400 rather than a cancellation that
// quietly drops the amount it believed it was recording (spec §6, "obsolete
// cancellation `refundAmount`" → 400). `.default({})` keeps the wire shape
// the FE already uses — a PATCH with no body at all.
export const CancelClientMembershipDto = z.object({}).strict().default({});

export type ApiCancelClientMembershipDto = z.infer<
  typeof CancelClientMembershipDto
>;

// MV2-573: a trainer letting a PENDING_PAYMENT_METHOD membership run before
// its card actually cleared (Aleksandar 1.2). No body — same empty-strict
// shape as CancelClientMembershipDto, for the same reason: a caller sending
// stray fields here almost certainly believes it does something it doesn't.
export const EnableClientMembershipDto = z.object({}).strict().default({});

export type ApiEnableClientMembershipDto = z.infer<
  typeof EnableClientMembershipDto
>;

// MV2-573 §2: the client's card is linked/on file → create the recurring
// Subscription (first charge = activation). No body, same reasoning as
// EnableClientMembershipDto.
export const ActivateRecurringMembershipDto = z.object({}).strict().default({});

export type ApiActivateRecurringMembershipDto = z.infer<
  typeof ActivateRecurringMembershipDto
>;

// MV2-573 §1.2: a trainer scheduling the first automatic charge attempt for
// a RECURRING membership run "on trust" (requiresCardLinkNow = false) — the
// only case with no card-linking trigger of its own. Bare civil day, like
// every other date-only field on this row.
// Live test 6 (O-c, Ruling AS) — `null` clears a scheduled day. Every refusal
// is a catalog key, so the 422 is translated (the ZodFilter renders only
// those): a missing date is Required, anything that is neither a day nor null
// an invalid date (a bad day string keeps CivilDateDto's own key).
export const ScheduleMembershipActivationDto = z.object({
  date: z.union([CivilDateDto, z.null()], {
    errorMap: (_issue, ctx) => ({
      message:
        ctx.data === undefined
          ? 'VALIDATION__Required'
          : 'VALIDATION__InvalidDate',
    }),
  }),
});

export type ApiScheduleMembershipActivationDto = z.infer<
  typeof ScheduleMembershipActivationDto
>;

// freezeStartDate defaults to today (in the organization's own zone) when
// omitted — see ClientMembershipController.freezeClientMembership.
export const FreezeClientMembershipDto = z.object({
  frozenDays: z.number().int().positive('VALIDATION__Positive'),
  freezeStartDate: CivilDateDto.optional(),
});

export type ApiFreezeClientMembershipDto = z.infer<
  typeof FreezeClientMembershipDto
>;

// Task 3 (membership-renewal plan) — "Obnovi istu": a new cycle of the SAME
// assignment (price, visits, trainer copied from the previous row, never
// re-read from the package). Staff choose the first day and (plan 1.4) may
// correct the visits and the discount of the new cycle — same bounds as an
// assignment; absent keeps the previous cycle's value (no default here).
// The package price is not a field: strict, so a caller sending a price or a
// package here believes it changes something it does not.
export const RenewClientMembershipDto = z
  .object({
    startDate: CivilDateDto,
    numberOfWorkouts: assignmentChoiceFields.numberOfWorkouts,
    discountPercent: assignmentChoiceFields.discountPercent.removeDefault(),
  })
  .strict();

export type ApiRenewClientMembershipDto = z.infer<
  typeof RenewClientMembershipDto
>;

/**
 * Task 9 (membership-renewal plan, Decision 7) — what the cycle being ended
 * today is worth. `FULL` leaves the contract value alone. `REDUCED` records
 * the difference to `reducedTo` (major units of the cycle's own currency) as
 * a discount with its reason; the server refuses a value below what was
 * already collected (money back is the separate refund action) or above the
 * price still in effect. Strict: a `FULL` body carrying an amount believes it
 * reduces something.
 */
export const EndCycleAmountDto = z.discriminatedUnion('amount', [
  z.object({ amount: z.literal('FULL') }).strict(),
  z
    .object({
      amount: z.literal('REDUCED'),
      reducedTo: z.number().nonnegative('VALIDATION__NonNegative'),
      reason: z.string().trim().min(1, 'VALIDATION__Required').max(500),
    })
    .strict(),
]);

export type ApiEndCycleAmountDto = z.infer<typeof EndCycleAmountDto>;

/** Task 9 — "Prekini ciklus": the cycle ends on the organization's today. */
export const EndClientMembershipCycleDto = EndCycleAmountDto;

export type ApiEndClientMembershipCycleDto = ApiEndCycleAmountDto;

/**
 * Task 9 — "Zameni odmah": the old cycle ends today (`oldCycle`, as above)
 * and the NEW package starts today, in one call. The new package is chosen
 * exactly as for an assignment; there is no start date to choose, and the
 * strict body refuses one rather than silently ignoring it.
 */
export const ReplaceClientMembershipNowDto = z
  .object({
    ...assignmentChoiceFields,
    oldCycle: EndCycleAmountDto,
  })
  .strict()
  .refine(onlyRecurringRequiresCardLink, {
    message: 'VALIDATION__RequiresCardLinkOnlyForRecurring',
    path: ['requiresCardLinkNow'],
  })
  .refine(onlyEligibleAutoRenew, {
    message: 'VALIDATION__AutoRenewOnlyForCardlessRecurring',
    path: ['autoRenew'],
  })
  .superRefine(refineTrainerFee);

export type ApiReplaceClientMembershipNowDto = z.infer<
  typeof ReplaceClientMembershipNowDto
>;
